import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { string, func } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Form,
  FieldTextInput,
  FieldSelect,
  H3,
  Button,
  FieldPhoneNumberWithCountryInput,
} from '../../components';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { useConfiguration } from '../../context/configurationContext';
import css from './ShippingAddressForm.module.css';
import { validateAddress } from './ShippingAddressForm.duck';
import isEqual from 'lodash/isEqual';
import getUsaStates from '../../translations/usaStates';
import getCanadaStates from '../../translations/canadaStates';

const usArmedForcesCities = [
  { code: 'APO', en: 'APO' },
  { code: 'FPO', en: 'FPO' },
  { code: 'DPO', en: 'DPO' },
];

/**
 * ShippingAddressFormComponent is a component that allows the user to enter their shipping address.
 * It is used in the ShippingDetailsForm component.
 * @param {Object} onValidateAddress - The function to call when the address is validated.
 * @param {Object} initialValues - The initial values for the form.
 * @param {Object} validateAddressInProgress - The state of the validate address in progress.
 * @param {Object} validateAddressSuccess - The state of the validate address success.
 * @param {Object} validateAddressError - The state of the validate address error.
 * @param {Object} className - The class name for the form.
 * @param {Object} rootClassName - The root class name for the form.
 * @param {boolean} showHeading - Whether to show the heading.
 * @param {Object} submitTitle - The title for the submit button.
 * @returns {JSX.Element} - The ShippingAddressFormComponent.
 */
export const ShippingAddressFormComponent = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const [submittedValues, setSubmittedValues] = useState({});

  const title = intl.formatMessage({ id: 'ShippingAddressForm.title' });
  const requireText = intl.formatMessage({ id: 'ShippingAddressForm.requireText' });
  const validateButton = intl.formatMessage({ id: 'ShippingAddressForm.validateButton' });
  const validateSuccessMsg = intl.formatMessage({ id: 'ShippingAddressForm.validateSuccess' });

  const nameLabel = intl.formatMessage({ id: 'ShippingAddressForm.nameLabel' });
  const namePlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.namePlaceholder' });
  const nameRequired = validators.required(requireText);

  const phoneLabel = intl.formatMessage({ id: 'ShippingAddressForm.phoneLabel' });
  const phonePlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.phonePlaceholder' });
  const invalidPhoneNumber = intl.formatMessage({ id: 'ShippingAddressForm.invalidPhoneNumber' });
  const phoneRequired = validators.required(requireText);
  const phoneValid = validators.validPhoneNumber(invalidPhoneNumber);

  const streetLabel = intl.formatMessage({ id: 'ShippingAddressForm.streetLabel' });
  const streetPlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.streetPlaceholder' });
  const streetRequired = validators.required(requireText);

  const aptLabel = intl.formatMessage({ id: 'ShippingAddressForm.aptLabel' });
  const aptPlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.aptPlaceholder' });

  const cityLabel = intl.formatMessage({ id: 'ShippingAddressForm.cityLabel' });
  const cityPlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.cityPlaceholder' });
  const cityRequired = validators.required(requireText);

  const zipLabel = intl.formatMessage({ id: 'ShippingAddressForm.zipLabel' });
  const zipPlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.zipPlaceholder' });
  const zipRequired = validators.required(requireText);

  const stateLabel = intl.formatMessage({ id: 'ShippingAddressForm.stateLabel' });
  const statePlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.statePlaceholder' });
  const stateRequired = validators.required(requireText);

  const countryLabel = intl.formatMessage({ id: 'ShippingAddressForm.countryLabel' });
  const countryPlaceholder = intl.formatMessage({ id: 'ShippingAddressForm.countryPlaceholder' });
  const countryRequired = validators.required(requireText);

  // Use the language set in config.localization.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(config.localization.locale).filter(elm =>
    ['CA', 'US'].includes(elm.code)
  );

  return (
    <FinalForm
      {...props}
      onSubmit={async values => {
        const { onSubmit } = props;
        const response = await onSubmit(values);

        if (response?.data?.data) {
          setSubmittedValues(values);
          props.successCallback?.();
        }
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          form,
          validateAddressError,
          validateAddressInProgress,
          showHeading = true,
          submitTitle,
          values,
          showShippingAddressFormError,
        } = fieldRenderProps;
        const submittedOnce = Object.keys(submittedValues).length > 0;
        const pristineSinceLastSubmit = submittedOnce && isEqual(values, submittedValues);
        const classes = classNames(rootClassName || css.root, className);

        const country = values?.country;
        const states = country ? (country === 'US' ? getUsaStates() : getCanadaStates()) : [];

        const isMilitaryState = country === 'US' && ['AA', 'AE', 'AP'].includes(values?.state);
        const prevIsMilitaryStateRef = useRef(isMilitaryState);

        // Clear city field when switching between military and non-military states
        useEffect(() => {
          if (prevIsMilitaryStateRef.current !== isMilitaryState && values?.city) {
            form.change('city', undefined);
          }
          prevIsMilitaryStateRef.current = isMilitaryState;
        }, [isMilitaryState, form, values?.city]);

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {showHeading && <H3>{title}</H3>}

            {showShippingAddressFormError && (
              <p className={css.error}>
                <FormattedMessage id="ShippingAddressForm.formError" />
              </p>
            )}

            <div className={css.formRow}>
              <FieldTextInput
                id={`${formId}.name`}
                name="name"
                className={css.fullField}
                type="text"
                label={nameLabel}
                placeholder={namePlaceholder}
                validate={nameRequired}
                onUnmount={() => form.change('name', undefined)}
              />
            </div>
            <div className={css.formRow}>
              <FieldPhoneNumberWithCountryInput
                id={`${formId}.phone`}
                autoComplete="shipping phoneNumber"
                name="phone"
                className={classNames(css.field, css.fullField)}
                label={phoneLabel}
                placeholder={phonePlaceholder}
                validate={validators.composeValidators(phoneRequired, phoneValid)}
                onUnmount={() => form.change('phone', undefined)}
              />
            </div>

            <div className={css.formRow}>
              <FieldTextInput
                id={`${formId}.street`}
                name="street1"
                className={css.field}
                type="text"
                label={streetLabel}
                placeholder={streetPlaceholder}
                validate={streetRequired}
                onUnmount={() => form.change('street1', undefined)}
              />

              <FieldTextInput
                id={`${formId}.apt`}
                name="streetNo"
                className={css.field}
                type="text"
                label={aptLabel}
                placeholder={aptPlaceholder}
                onUnmount={() => form.change('streetNo', undefined)}
              />
            </div>
            <div className={css.formRow}>
              <FieldSelect
                id={`${formId}.country`}
                name="country"
                className={css.field}
                label={countryLabel}
                validate={countryRequired}
              >
                <option disabled value="">
                  {countryPlaceholder}
                </option>
                {countryCodes.map(country => {
                  return (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  );
                })}
              </FieldSelect>

              {isMilitaryState ? (
                <FieldSelect
                  id={`${formId}.city`}
                  name="city"
                  className={css.field}
                  label={cityLabel}
                  validate={cityRequired}
                >
                  <option disabled value="">
                    {cityPlaceholder}
                  </option>
                  {usArmedForcesCities.map(city => (
                    <option key={city.code} value={city.code}>
                      {city.en}
                    </option>
                  ))}
                </FieldSelect>
              ) : (
                <FieldTextInput
                  id={`${formId}.city`}
                  name="city"
                  className={css.field}
                  type="text"
                  label={cityLabel}
                  placeholder={cityPlaceholder}
                  validate={cityRequired}
                  onUnmount={() => form.change('city', undefined)}
                />
              )}
            </div>
            <div className={css.formRow}>
              <FieldSelect
                id={`${formId}.state`}
                name="state"
                className={css.field}
                label={stateLabel}
                validate={stateRequired}
              >
                <option disabled value="">
                  {statePlaceholder}
                </option>
                {states.map(state => {
                  return (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  );
                })}
              </FieldSelect>

              <FieldTextInput
                id={`${formId}.zip`}
                name="zip"
                className={css.field}
                type="text"
                label={zipLabel}
                placeholder={zipPlaceholder}
                validate={zipRequired}
                onUnmount={() => form.change('zip', undefined)}
              />
            </div>

            {validateAddressError?.error?.message && (
              <p className={css.error}>
                {validateAddressError.error.message ?? (
                  <FormattedMessage id="ShippingAddressForm.validateError" />
                )}
              </p>
            )}

            <Button
              className={css.validateButton}
              type="submit"
              inProgress={validateAddressInProgress}
              ready={pristineSinceLastSubmit}
            >
              {submitTitle ?? validateButton}
            </Button>
          </Form>
        );
      }}
    />
  );
};

ShippingAddressFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  successCallback: func,
};

const mapStateToProps = state => {
  const {
    initialValues,
    validateAddressInProgress,
    validateAddressSuccess,
    validateAddressError,
  } = state.ShippingAddressForm;

  return {
    initialValues,
    validateAddressInProgress,
    validateAddressSuccess,
    validateAddressError,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmit: address => dispatch(validateAddress(address)),
});

const ShippingAddressForm = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ShippingAddressFormComponent);

export default ShippingAddressForm;
